import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Theaters as ShowTimeIcon } from '@mui/icons-material';
import { useAuthContext } from '../../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const ShowTimeCodeEntry = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuthContext();
  const fromMym = location.state?.from === 'mym' || location.state?.reason === 'code_required';

  React.useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login', { state: { from: '/showtime' } });
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!code?.trim()) {
      setError('Please enter a code');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE}/api/show-times/validate-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ code: code.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Invalid code');
        return;
      }
      if (data.needsPayment) {
        navigate(`/showtime/payment/${data.showTime.id}`);
        return;
      }
      if (data.redirectTo) {
        navigate(data.redirectTo);
      } else {
        navigate('/mindyourmusic');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated || !user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: '#FF6B9D' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(180deg, #0a0015 0%, #1a0033 50%, #0a0015 100%)',
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 440,
          width: '100%',
          p: 4,
          backgroundColor: 'rgba(30,30,50,0.95)',
          border: '2px solid rgba(255,107,157,0.4)',
          borderRadius: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <ShowTimeIcon sx={{ fontSize: 48, color: '#FF6B9D', mr: 2 }} />
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
            Enter Show Time Code
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: '#B0B0B0', mb: 3 }}>
          {fromMym ? 'A private show is in progress. Enter the code to join.' : 'Enter the code provided for this private show to join.'}
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. LionTiger42"
            disabled={loading}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                backgroundColor: 'rgba(0,0,0,0.3)',
                '& fieldset': { borderColor: 'rgba(255,107,157,0.5)' },
                '&:hover fieldset': { borderColor: '#FF6B9D' },
                '&.Mui-focused fieldset': { borderColor: '#FF6B9D' },
              },
            }}
          />
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              background: 'linear-gradient(135deg, #FF6B9D 0%, #E91E63 100%)',
              color: '#fff',
              py: 1.5,
              fontWeight: 700,
              '&:hover': { background: 'linear-gradient(135deg, #E91E63 0%, #C2185B 100%)' },
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Join Show'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default ShowTimeCodeEntry;
